/**
 * Bond Tube Offset Calculator
 *
 * Calculates the adjusted start position for arrows originating from bonds,
 * especially double/triple bonds where the arrow should start from the
 * closest bond line (tube) rather than the bond midpoint.
 *
 * This logic was previously duplicated in ArrowPreviewLine (3 times) and
 * other arrow renderers. Now it's a single, testable utility.
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { FormatOptions } from '@alchemiesolns/svg-format';

/**
 * Information about bond tubes (parallel bond lines)
 */
export interface BondTubeInfo {
  bondId: string;
  tubes: Array<{ offset: number }>;
}

/**
 * Result of bond tube offset calculation
 */
export interface BondTubeOffsetResult {
  /** Adjusted X position */
  x: number;
  /** Adjusted Y position */
  y: number;
  /** The tube offset that was selected */
  selectedTubeOffset: number;
}

/**
 * Calculate the correct bond tube offset for an arrow starting from a bond
 *
 * For single bonds, returns the bond midpoint unchanged.
 * For double bonds, uses the second bond line (non-primary tube).
 * For triple bonds, finds which parallel bond line (tube) is closest to the destination.
 *
 * @param bondId - ID of the source bond
 * @param bondMidpoint - Bond midpoint position in SVG coordinates
 * @param dragStartPos - Where user started dragging (in SVG coordinates)
 * @param bondTubeInfo - Tube information from bond recipe
 * @param workspace - Molecular workspace for atom queries
 * @param destinationPos - Optional destination position (for triple bonds)
 * @returns Adjusted position that touches the correct bond tube
 */
export function calculateClosestBondTube(
  bondId: string,
  bondMidpoint: { x: number; y: number },
  dragStartPos: { x: number; y: number },
  bondTubeInfo: BondTubeInfo | undefined,
  workspace: MolecularWorkspace,
  destinationPos?: { x: number; y: number }
): BondTubeOffsetResult {
  // Get bond from workspace
  const bond = workspace.getBond(bondId);
  if (!bond) {
    return { x: bondMidpoint.x, y: bondMidpoint.y, selectedTubeOffset: 0 };
  }

  // For single bonds, no offset needed
  if (bond.order === 1) {
    return { x: bondMidpoint.x, y: bondMidpoint.y, selectedTubeOffset: 0 };
  }

  // For double/triple bonds, need tube information
  if (!bondTubeInfo || bondTubeInfo.tubes.length === 0) {
    return { x: bondMidpoint.x, y: bondMidpoint.y, selectedTubeOffset: 0 };
  }

  // Get bond atoms
  const atom1 = workspace.getAtom(bond.a1.id);
  const atom2 = workspace.getAtom(bond.a2.id);

  if (!atom1 || !atom2) {
    return { x: bondMidpoint.x, y: bondMidpoint.y, selectedTubeOffset: 0 };
  }

  // Calculate perpendicular direction to bond
  // Use original chemistry coordinates (not SVG-flipped) to match computeBondEndPoints
  // computeBondEndPoints uses camDir [0,0,1] × dir = [-dy, dx, 0]
  const bondDx = atom2.x - atom1.x;
  const bondDy = atom2.y - atom1.y;
  const bondLength = Math.sqrt(bondDx * bondDx + bondDy * bondDy);

  if (bondLength === 0) {
    return { x: bondMidpoint.x, y: bondMidpoint.y, selectedTubeOffset: 0 };
  }

  // Perpendicular vector: cross([0,0,1], [dx,dy,0]) = [-dy, dx, 0]
  // In SVG space (y-flipped), this becomes [-dy, -dx] when applied
  const perpX = -bondDy / bondLength;
  const perpY = -bondDx / bondLength;

  // Select tube based on bond order
  let selectedTubeOffset = 0;

  if (bond.order === 2) {
    // DOUBLE BOND: Always use the second bond line (non-primary tube)
    // Tubes are typically ordered: [primary (0 or near-0), secondary (offset)]
    // Find the tube with the largest absolute offset (the "second" line)
    let maxAbsOffset = 0;
    for (const tube of bondTubeInfo.tubes) {
      if (Math.abs(tube.offset) > maxAbsOffset) {
        maxAbsOffset = Math.abs(tube.offset);
        selectedTubeOffset = tube.offset;
      }
    }
  } else if (bond.order === 3) {
    // TRIPLE BOND: Use the tube closest to the destination
    if (destinationPos) {
      // Calculate perpendicular distance from bond midpoint to destination
      const toDestX = destinationPos.x - bondMidpoint.x;
      const toDestY = destinationPos.y - bondMidpoint.y;
      const destPerpDistance = toDestX * perpX + toDestY * perpY;

      // Find the closest tube to the destination point
      let minDistance = Infinity;
      for (const tube of bondTubeInfo.tubes) {
        const distance = Math.abs(tube.offset - destPerpDistance);
        if (distance < minDistance) {
          minDistance = distance;
          selectedTubeOffset = tube.offset;
        }
      }
    } else {
      // Fallback: use outermost tube (largest absolute offset)
      let maxAbsOffset = 0;
      for (const tube of bondTubeInfo.tubes) {
        if (Math.abs(tube.offset) > maxAbsOffset) {
          maxAbsOffset = Math.abs(tube.offset);
          selectedTubeOffset = tube.offset;
        }
      }
    }
  } else {
    // Order > 3: fallback to closest to drag start
    const toStartX = dragStartPos.x - bondMidpoint.x;
    const toStartY = dragStartPos.y - bondMidpoint.y;
    const perpDistance = toStartX * perpX + toStartY * perpY;

    let minDistance = Infinity;
    for (const tube of bondTubeInfo.tubes) {
      const distance = Math.abs(tube.offset - perpDistance);
      if (distance < minDistance) {
        minDistance = distance;
        selectedTubeOffset = tube.offset;
      }
    }
  }

  // Calculate adjusted position using the tube offset
  const adjustedX = bondMidpoint.x + perpX * selectedTubeOffset;
  const adjustedY = bondMidpoint.y + perpY * selectedTubeOffset;

  return {
    x: adjustedX,
    y: adjustedY,
    selectedTubeOffset: selectedTubeOffset,
  };
}

/**
 * Calculate bond midpoint position between two atoms closer to a specific atom
 *
 * For bond-based arrows (BA, BB), the arrow should start between the bond
 * midpoint and the atom closer to where the user started dragging.
 * Takes atom radii into account to properly handle small carbons vs larger heteroatoms.
 *
 * @param atom1Pos - First atom position
 * @param atom2Pos - Second atom position
 * @param dragStartPos - Where user started dragging
 * @param atom1Radius - Visual radius of first atom (paper carbon vs text atom)
 * @param atom2Radius - Visual radius of second atom
 * @returns Position between closer atom edge and bond midpoint
 */
export function calculateBondStartPosition(
  atom1Pos: { x: number; y: number },
  atom2Pos: { x: number; y: number },
  dragStartPos: { x: number; y: number },
  atom1Radius: number = 5,
  atom2Radius: number = 5
): { x: number; y: number; closerAtom: 1 | 2 } {
  // Calculate bond midpoint
  const bondMidX = (atom1Pos.x + atom2Pos.x) / 2;
  const bondMidY = (atom1Pos.y + atom2Pos.y) / 2;

  // Determine which atom is closer to drag start
  const dist1 = Math.hypot(dragStartPos.x - atom1Pos.x, dragStartPos.y - atom1Pos.y);
  const dist2 = Math.hypot(dragStartPos.x - atom2Pos.x, dragStartPos.y - atom2Pos.y);

  const closerAtomPos = dist1 < dist2 ? atom1Pos : atom2Pos;
  const closerAtomRadius = dist1 < dist2 ? atom1Radius : atom2Radius;
  const closerAtom = dist1 < dist2 ? 1 : 2;

  // Calculate direction from closer atom toward bond midpoint
  const dx = bondMidX - closerAtomPos.x;
  const dy = bondMidY - closerAtomPos.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) {
    // Atoms are at same position (shouldn't happen)
    return { x: closerAtomPos.x, y: closerAtomPos.y, closerAtom };
  }

  // Start position: from atom edge (atom center + radius) toward bond midpoint
  // Then take midpoint between that edge position and bond midpoint
  const atomEdgeX = closerAtomPos.x + (dx / length) * closerAtomRadius;
  const atomEdgeY = closerAtomPos.y + (dy / length) * closerAtomRadius;

  // Position start point between the atom edge and bond midpoint
  const startX = (atomEdgeX + bondMidX) / 2;
  const startY = (atomEdgeY + bondMidY) / 2;

  return { x: startX, y: startY, closerAtom };
}

/**
 * Helper to determine visual radius of an atom
 * Paper carbons are smaller (radius * carbon.scale) than text atoms (radius)
 */
function getAtomVisualRadius(atomSymbol: string, format: FormatOptions): number {
  // Check if it's a carbon that would be rendered as paper carbon
  // Simplified check: C or CH atoms are paper carbons
  const isPaperCarbon = atomSymbol === 'C' || atomSymbol === 'CH';

  if (isPaperCarbon) {
    return format.atom.radius * format.atom.carbon.scale;
  } else {
    return format.atom.radius;
  }
}

/**
 * Calculate adjusted start position for bond arrows (BA, BB)
 *
 * Combines bond start position calculation with tube offset for double/triple bonds.
 * Takes atom radii into account (small carbons vs larger heteroatoms).
 * - Double bonds: uses second bond line
 * - Triple bonds: uses line closest to destination
 *
 * @param bondId - ID of the source bond
 * @param dragStartPos - Where user started dragging (used to determine closer atom)
 * @param bondTubeInfo - Tube information from bond recipe
 * @param workspace - Molecular workspace
 * @param format - Format options for atom radius calculations
 * @param destinationPos - Optional destination position (for triple bonds)
 * @returns Adjusted start position between atom edge and bond midpoint, with tube offset
 */
export function calculateBondArrowStart(
  bondId: string,
  dragStartPos: { x: number; y: number },
  bondTubeInfo: BondTubeInfo | undefined,
  workspace: MolecularWorkspace,
  format: FormatOptions,
  destinationPos?: { x: number; y: number }
): BondTubeOffsetResult {
  const bond = workspace.getBond(bondId);
  if (!bond) {
    return { x: dragStartPos.x, y: dragStartPos.y, selectedTubeOffset: 0 };
  }

  const atom1 = workspace.getAtom(bond.a1.id);
  const atom2 = workspace.getAtom(bond.a2.id);

  if (!atom1 || !atom2) {
    return { x: dragStartPos.x, y: dragStartPos.y, selectedTubeOffset: 0 };
  }

  // Convert to SVG coordinates
  const atom1Pos = { x: atom1.x, y: -atom1.y };
  const atom2Pos = { x: atom2.x, y: -atom2.y };

  // Calculate atom visual radii (paper carbons are smaller)
  const atom1Radius = getAtomVisualRadius(atom1.symbol, format);
  const atom2Radius = getAtomVisualRadius(atom2.symbol, format);

  // Calculate base start position (between closer atom edge and bond midpoint)
  const baseStart = calculateBondStartPosition(atom1Pos, atom2Pos, dragStartPos, atom1Radius, atom2Radius);

  // For single bonds, we're done
  if (bond.order === 1) {
    return { x: baseStart.x, y: baseStart.y, selectedTubeOffset: 0 };
  }

  // For double/triple bonds, apply tube offset to the base start position
  const bondMidpoint = {
    x: (atom1Pos.x + atom2Pos.x) / 2,
    y: (atom1Pos.y + atom2Pos.y) / 2,
  };

  // Get tube offset based on bond order and destination
  const tubeResult = calculateClosestBondTube(
    bondId,
    bondMidpoint,
    dragStartPos,
    bondTubeInfo,
    workspace,
    destinationPos
  );

  // Calculate perpendicular for applying offset to baseStart
  const bondDx = atom2.x - atom1.x;
  const bondDy = atom2.y - atom1.y;
  const bondLength = Math.sqrt(bondDx * bondDx + bondDy * bondDy);

  if (bondLength === 0) {
    return tubeResult;
  }

  const perpX = -bondDy / bondLength;
  const perpY = -bondDx / bondLength;

  // Apply tube offset to base start position
  return {
    x: baseStart.x + perpX * tubeResult.selectedTubeOffset,
    y: baseStart.y + perpY * tubeResult.selectedTubeOffset,
    selectedTubeOffset: tubeResult.selectedTubeOffset,
  };
}
